import { Node } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

/** Delete range for a page break, including the empty paragraph insertPageBreak adds after it. */
export const getPageBreakDeleteRange = (
  doc: ProseMirrorNode,
  pos: number
): { from: number; to: number } | null => {
  const node = doc.nodeAt(pos);
  if (!node || node.type.name !== 'pageBreak') return null;

  let deleteTo = pos + node.nodeSize;
  const nodeAfter = doc.resolve(deleteTo).nodeAfter;
  if (nodeAfter?.type.name === 'paragraph' && nodeAfter.content.size === 0) {
    deleteTo += nodeAfter.nodeSize;
  }
  return { from: pos, to: deleteTo };
};

export const PageBreakNode = Node.create({
  name: 'pageBreak',

  group: 'block',
  atom: true,
  selectable: true,

  parseHTML() {
    // Priority above DivNode's generic `div` rule so saved page breaks round-trip as pageBreak nodes.
    const priority = 60;
    // The node is an atom, so a match drops the element's children. Older documents wrap whole
    // sections in div.page-break, so only an empty marker may parse as a page break.
    const isEmptyMarker = (element: HTMLElement) =>
      element.children.length === 0 && !element.textContent?.trim();
    const hasBreakStyle = (element: HTMLElement) =>
      /break-(before|after)/.test(element.getAttribute('style') || '');
    return [
      {
        tag: 'div',
        priority,
        getAttrs: (element) => {
          const el = element as HTMLElement;
          const isMarker =
            el.hasAttribute('data-page-break') ||
            el.classList.contains('page-break') ||
            hasBreakStyle(el);
          return isMarker && isEmptyMarker(el) ? {} : false;
        }
      }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      {
        ...HTMLAttributes,
        'data-page-break': 'true',
        style: 'page-break-before: always;'
      }
    ];
  },

  addCommands() {
    return {
      insertPageBreak:
        () =>
        ({ commands }) =>
          commands.insertContent([
            {
              type: this.name
            },
            {
              type: 'paragraph'
            }
          ]),
      deletePageBreak:
        (pos?: number) =>
        ({ state, dispatch }) => {
          const deletePos =
            pos ??
            (state.selection instanceof NodeSelection &&
            state.selection.node.type.name === 'pageBreak'
              ? state.selection.from
              : null);
          if (deletePos === null) return false;

          const range = getPageBreakDeleteRange(state.doc, deletePos);
          if (!range) return false;

          if (dispatch) {
            dispatch(state.tr.delete(range.from, range.to));
          }
          return true;
        }
    };
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () => this.editor.commands.deletePageBreak(),
      Delete: () => this.editor.commands.deletePageBreak()
    };
  },

  addNodeView() {
    return ({ getPos, editor }) => {
      const container = document.createElement('div');
      // Do not set data-page-break on the editor chrome — the backend stylesheet hides
      // [data-page-break] for PDF output (height: 0, children display: none).
      container.setAttribute('data-editor-page-break', 'true');
      container.setAttribute('contenteditable', 'false');
      container.className =
        'group my-6 flex items-center text-neutral-500 dark:text-neutral-400 text-[10px] font-semibold tracking-[0.2em] uppercase relative';

      const createLine = () => {
        const line = document.createElement('div');
        line.className = 'flex-1 border-t border-dashed border-neutral-300 dark:border-neutral-600';
        return line;
      };

      const label = document.createElement('div');
      label.className =
        'mx-3 px-3 py-1 rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-700';
      label.append('Page break');
      const hint = document.createElement('span');
      hint.textContent = ' · pages redraw on save';
      hint.className =
        'normal-case tracking-normal font-normal text-neutral-400 dark:text-neutral-500';
      label.append(hint);

      const deleteBtn = document.createElement('button');
      deleteBtn.type = 'button';
      deleteBtn.title = 'Remove page break';
      deleteBtn.className =
        'ml-2 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 cursor-pointer shrink-0';
      deleteBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" class="size-3" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>';
      deleteBtn.addEventListener('mousedown', (e) => {
        e.preventDefault();
        const pos = getPos();
        if (typeof pos === 'number') {
          editor.commands.deletePageBreak(pos);
        }
      });

      container.append(createLine(), label, createLine(), deleteBtn);

      return {
        dom: container
      };
    };
  }
});
